import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AuthBrandHeader } from './AuthBrandHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background px-4 py-12"
        style={{ backgroundImage: 'var(--surface-pattern)' }}
      >
        <div className="w-full max-w-md">
          <AuthBrandHeader />
          <Card className="border-border bg-card shadow-none">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-foreground">Check your email</CardTitle>
              <CardDescription>
                We sent a verification code to {email}. Use it to reset your password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <Link to={`/reset-password?email=${encodeURIComponent(email)}`}>
                  Enter verification code
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-4 py-12"
      style={{ backgroundImage: 'var(--surface-pattern)' }}
    >
      <div className="w-full max-w-md">
        <AuthBrandHeader />
        <Card className="border-border bg-card shadow-none">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-foreground">Forgot password</CardTitle>
            <CardDescription>
              Enter your email and we will send you a verification code.
            </CardDescription>
          </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full h-10"
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Send verification code'}
            </Button>
          </form>
          <p className="mt-4 text-sm text-muted-foreground">
            <Link to="/login" className="hover:underline">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
